import { IconChevronRight, IconPower, IconShieldCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { SetupStep } from "./ai-policy-options";

/** Step-aware footer actions, including the disable-AI confirmation in edit mode. */
export function AiPolicyDialogFooter({
  isEditing,
  step,
  setStep,
  eligibleCampaigns,
  workspaceShopCount,
  disableConfirmOpen,
  setDisableConfirmOpen,
  disablePending,
  onDisable,
  validAcos,
  policyLoading,
  savePending,
  onSave,
  resetAndClose,
}: {
  isEditing: boolean;
  step: SetupStep;
  setStep: (step: SetupStep) => void;
  eligibleCampaigns: number;
  workspaceShopCount: number;
  disableConfirmOpen: boolean;
  setDisableConfirmOpen: (open: boolean) => void;
  disablePending: boolean;
  onDisable: () => void;
  validAcos: boolean;
  policyLoading: boolean;
  savePending: boolean;
  onSave: () => void;
  resetAndClose: () => void;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="flex h-[62px] shrink-0 items-center justify-between border-t border-line px-[22px]">
      {isEditing && step === "policy" ? (
        <>
          <ControlButton
            className="h-8 rounded-[8px] px-2 text-[11px] text-red-11 hover:bg-red-subtle hover:text-red-11 focus-visible:ring-red"
            controlSize="toolbar"
            variant="ghost"
            onClick={() => setDisableConfirmOpen(true)}
          >
            <IconPower data-icon="inline-start" /> {t("policyDialog.disable.button")}
          </ControlButton>
          <AlertDialog open={disableConfirmOpen} onOpenChange={setDisableConfirmOpen}>
            <AlertDialogPopup className="max-w-[460px]">
              <AlertDialogHeader className="gap-2 pb-5">
                <AlertDialogTitle>{t("policyDialog.disable.confirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription className="text-[12px] leading-5">
                  {t("policyDialog.disable.description", {
                    scope:
                      workspaceShopCount === 1
                        ? t("policyDialog.disable.scope.oneShop")
                        : workspaceShopCount > 1
                          ? t("policyDialog.disable.scope.allShops", {
                              shopCount: workspaceShopCount,
                            })
                          : t("policyDialog.disable.scope.thisWorkspace"),
                  })}
                </AlertDialogDescription>
                <div className="mt-2 space-y-2 rounded-[10px] border border-line bg-canvas p-3 text-[11px] leading-4 text-muted-foreground">
                  <p className="flex gap-2">
                    <IconPower className="mt-0.5 size-3.5 shrink-0 text-red-11" />
                    {t("policyDialog.disable.stopNote")}
                  </p>
                  <p className="flex gap-2">
                    <IconShieldCheck className="mt-0.5 size-3.5 shrink-0 text-green-11" />
                    {t("policyDialog.disable.keepNote")}
                  </p>
                </div>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogClose
                  render={<ControlButton controlSize="toolbar" variant="outline" />}
                >
                  {t("policyDialog.disable.keepActive")}
                </AlertDialogClose>
                <ControlButton
                  controlSize="toolbar"
                  loading={disablePending}
                  variant="destructive"
                  onClick={() => onDisable()}
                >
                  {t("policyDialog.disable.button")}
                </ControlButton>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        </>
      ) : (
        <p className="text-[9px] text-muted-foreground">
          {step === "policy"
            ? t("policyDialog.footer.changeAnytime")
            : step === "review"
              ? t("policyDialog.footer.willInherit", { eligibleCampaigns })
              : t("policyDialog.footer.firstRunsBegin")}
        </p>
      )}
      <div className="flex items-center gap-2">
        {isEditing && step === "policy" ? (
          <>
            <ControlButton
              className="h-8 rounded-[8px] px-3 text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
              controlSize="toolbar"
              variant="outline"
              onClick={resetAndClose}
            >
              {t("policyDialog.buttons.cancel")}
            </ControlButton>
            <ControlButton
              className="h-8 rounded-[8px] bg-ink px-3 text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
              controlSize="toolbar"
              disabled={!validAcos || policyLoading}
              loading={savePending}
              onClick={() => onSave()}
            >
              {t("policyDialog.buttons.saveChanges")} <IconChevronRight className="size-3" />
            </ControlButton>
          </>
        ) : step === "policy" ? (
          <>
            <ControlButton
              className="h-8 rounded-[8px] px-3 text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
              controlSize="toolbar"
              variant="outline"
              onClick={resetAndClose}
            >
              {t("policyDialog.buttons.cancel")}
            </ControlButton>
            <ControlButton
              className="h-8 rounded-[8px] bg-ink px-3 text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
              controlSize="toolbar"
              disabled={!validAcos || policyLoading}
              onClick={() => setStep("review")}
            >
              {t("policyDialog.buttons.review")} <IconChevronRight className="size-3" />
            </ControlButton>
          </>
        ) : step === "review" ? (
          <>
            <ControlButton
              className="h-8 rounded-[8px] px-3 text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
              controlSize="toolbar"
              variant="outline"
              onClick={() => setStep("policy")}
            >
              {t("policyDialog.buttons.back")}
            </ControlButton>
            <ControlButton
              className="h-8 rounded-[8px] bg-ink px-3 text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
              controlSize="toolbar"
              loading={savePending}
              onClick={() => onSave()}
            >
              {t("policyDialog.buttons.enableAi")} <IconChevronRight className="size-3" />
            </ControlButton>
          </>
        ) : (
          <ControlButton
            className="h-8 rounded-[8px] bg-ink px-3 text-[11px] focus-visible:ring-0 focus-visible:ring-offset-0"
            controlSize="toolbar"
            onClick={resetAndClose}
          >
            {t("policyDialog.buttons.done")}
          </ControlButton>
        )}
      </div>
    </div>
  );
}
